import { useParams } from "react-router-dom";
import {
  useLeadsContainerStore,
  useLeadsStore,
} from "../../../../stores/LeadsStore";
import { useCallback, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { exportPrm, exportUserPrm } from "../../../../api/PRMAPI";
import {
  Button,
  Popover,
  SegmentedControl,
  Stack,
  Tooltip,
} from "@mantine/core";
import {
  IconChevronRight,
  IconCircleArrowDownFilled,
} from "@tabler/icons-react";
import { useProfileStore } from "../../../../stores/UserStore";
import { deselectAll } from "../utils";
import { transformFilterToServer } from "../../../../functions/utilFunctions";

const defaults = ["opener", "clicker", "answerer", "to_call", "out_of_office"];

const LeadsExport = ({ handleClose, tableFilter, tableSelection, handleFinish }) => {
  const lang = useLanguage((s) => s.language);
  const query = useLeadsStore((s) => s.query);

  const globalFilter = useLeadsStore((s) => s.filter);
  const globalSelectedIds = useLeadsContainerStore((s) => s.selectedIds);
  const unselectedIds = useLeadsContainerStore((s) => s.unselectedIds);
  const selectedColumns = useLeadsContainerStore((s) => s.selectedColumns);

  const selectedIds = tableSelection || globalSelectedIds;
  const filter = tableFilter ? transformFilterToServer(tableFilter) : globalFilter;

  const { userId } = useParams();
  const [format, setFormat] = useState("xlsx");
  const [loading, setLoading] = useState(false);
  const [isSelection, setIsSelection] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [isAll, setIsAll] = useState(false);

  const getExportedName = useCallback(() => {
    let profile = useProfileStore.getState().profile;
    let containers = useLeadsContainerStore.getState().containers;
    if (isAll) {
      return `All PRM export - #${profile.id}`;
    }

    if (selectedColumns.length > 0) {
      return `PRM export _ ${selectedColumns
        .map((x) => {
          return containers.find((container) => container.id === x)?.name || "";
        })
        .join(", ")} - #${profile.id}`;
    }

    return `PRM export`;
  }, [isAll, selectedColumns]);

  const handleExport = (type) => {
    setLoading(true);
    setIsSelection(type === 1);
    setIsAll(type === 0);
    setIsFilter(type === "filter");

    const exportFunc = userId ? exportUserPrm : exportPrm;

    let mainFilterValues = [];
    let excluded = [];
    if (selectedColumns.length > 0) {
      selectedColumns.forEach((col) => {
        if (defaults.includes(col)) {
          let v = [
            {
              field_name: "status",
              type: "equals",
              value: col,
            },
            {
              field_name: "custom_status",
              type: "equals",
              value: "null",
            },
          ];

          if (query) {
            v.push({
              field_name: "any_datafield",
              type: "contains",
              value: query,
            });
          }

          if (filter) {
            v.push(filter);
          }

          mainFilterValues.push({
            mode: "and",
            values: v,
          });
        } else {
          let v = [
            {
              field_name: "custom_status",
              type: "equals",
              value: col.toString(),
            },
          ];

          if (filter) {
            v.push(filter);
          }

          mainFilterValues.push({
            mode: "and",
            values: v,
          });
        }
        if (unselectedIds[col]) {
          excluded.push(...unselectedIds[col]);
        }
      });
    }

    let parsedFilter = {
      mode: "or",
      values: mainFilterValues,
    };

    let justFilter = {
      mode: "and",
      values: [],
    };
    if (type === "filter" && filter) {
      justFilter.values.push(filter);
    }
    if (type === "filter" && query) {
      justFilter.values.push({
        field_name: "any_datafield",
        type: "contains",
        value: query,
      });
    }

    exportFunc({
      user_id: parseInt(userId),
      file_type: format,
      filter: type === "filter" ? justFilter : parsedFilter,
      contact_ids:
        type === 0 ? [] : Object.values(selectedIds).flatMap((x) => x),
      excluded_contact_ids: type === 0 ? [] : excluded,
      name: getExportedName(),
    })
      .then((response) => {
        handleClose?.();
        if (isSelection) {
          deselectAll();
        }
        handleFinish?.();
      })
      .finally(() => {
        setLoading(false);
        setIsAll(false);
        setIsSelection(false);
        setIsFilter(false);
      });
  };

  const canExport = () => {
    if (Object.values(selectedIds).flatMap((x) => x).length > 0) {
      return true;
    }
    if (selectedColumns.length > 0) {
      return true;
    }

    return false;
  };

  return (
    <Popover zIndex={300} position="right-start">
      <Popover.Target>
        <Button
          variant="light"
          justify="start"
          leftSection={<IconCircleArrowDownFilled size={16} />}
          rightSection={<IconChevronRight size={16} />}
          styles={{
            label: {
              marginRight: "auto",
            },
          }}
        >
          {lang.prm.toolbar.export}
        </Button>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap={"xs"} w={250}>
          <SegmentedControl
            data={[
              {
                label: ".xlsx",
                value: "xlsx",
              },
              {
                label: ".csv",
                value: "csv",
              },
              {
                label: ".json",
                value: "json",
              },
            ]}
            value={format}
            onChange={setFormat}
          />

          <Button
            loading={isSelection}
            disabled={!canExport() || loading}
            onClick={() => handleExport(1)}
          >
            {lang.prm.toolbar.export_selected}
          </Button>

          <Button
            loading={isFilter}
            disabled={
              loading || ((!filter || filter?.values?.length === 0) && !query)
            }
            onClick={() => handleExport("filter")}
          >
            {lang.prm.toolbar.export_filter}
          </Button>

          <Button
            loading={isAll}
            disabled={loading}
            onClick={() => handleExport(0)}
          >
            {lang.prm.toolbar.export_all}
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default LeadsExport;
